// Based on https://codesandbox.io/embed/framer-motion-drag-slider-3mggu
import { useInView } from 'react-intersection-observer'
import { useImagesData } from '../../../hooks/use-image-data/useImageData'
import { fullwidthGridColumn, grid } from '../../../utils/grid'
import { motion, useMotionValue } from 'framer-motion'
import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react'
import Colors from '../../../tokens/Colors'
import { mediaQueries } from '../../../utils/mediaQueries'
import { rem } from '../../../utils/rem'
import { spacing } from '../../../utils/spacing'
import { spacings } from '../../../tokens/Spacings'
import styled from 'styled-components'
import useDictionary from '../../../hooks/use-dictionary/useDictionary'
import { useWindowSize } from '@react-hook/window-size'
import Img from 'gatsby-image'
import {
  lineAnimation,
  slideImageAnimation,
  titleAnimation,
  wrapperAnimation,
} from './animations'
import { useAppState } from '../../../contexts/app-state/AppStateContext'
import { Actions } from '../../../contexts/app-state/AppStateReducer'

export interface CarouselProps {
  className?: string
  images: string[]
  name: string
}

const Carousel: FunctionComponent<CarouselProps> = ({
  className,
  images,
  name,
}) => {
  const [, dispatch] = useAppState()
  const dictionary = useDictionary()
  const sliderRef = useRef<HTMLDivElement | null>(null)
  const sliderWrapperRef = useRef<HTMLDivElement | null>(null)
  const [sliderWidth, setSliderWidth] = useState(0)
  const [sliderOffset, setSliderOffset] = useState(0)
  const [sliderWrapperWidth, setSliderWrapperWidth] = useState(0)
  const [sliderConstraints, setSliderConstraints] = useState(0)
  const [windowWidth, windowHeight] = useWindowSize()
  const x = useMotionValue(0)
  const imageSources = useImagesData(
    images.map((image) => `carousel/${name}/${image}`),
  )

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const onHoverStart = useCallback(() => {
    dispatch({
      payload: 'maximize',
      type: Actions.SET_CURSOR_OVERLAY_TYPE,
    })
  }, [])

  const onHoverEnd = useCallback(() => {
    dispatch({
      payload: null,
      type: Actions.SET_CURSOR_OVERLAY_TYPE,
    })
  }, [])

  const onTap = useCallback((src: string) => {
    dispatch({
      payload: src,
      type: Actions.SET_ZOOM_IMAGE_SRC,
    })
  }, [])

  useEffect(() => {
    const calcSliderWrapperWidth = (): void => {
      if (sliderWrapperRef.current) {
        setSliderWrapperWidth(sliderWrapperRef.current.clientWidth)
      }
    }

    const calcSliderOffset = (): void => {
      if (sliderWrapperRef.current) {
        const paddingLeft = parseFloat(
          window
            .getComputedStyle(sliderWrapperRef.current)
            .getPropertyValue('padding-left'),
        )
        setSliderOffset(paddingLeft)
      }
    }

    const calcSliderWidth = (): void => {
      if (sliderRef.current) {
        setSliderWidth(sliderRef.current.clientWidth)
      }
    }

    const calcSliderConstraints = (): void => {
      setSliderConstraints(sliderWidth - sliderWrapperWidth + sliderOffset * 2)
    }

    const onResize = (): void => {
      calcSliderWrapperWidth()
      calcSliderWidth()
      calcSliderOffset()
      calcSliderConstraints()
    }

    onResize()
  }, [
    sliderRef,
    sliderWidth,
    sliderWrapperWidth,
    sliderWrapperRef,
    windowWidth,
    windowHeight,
  ])

  return (
    <StyledCarousel className={className} ref={ref}>
      <TitleWrapper>
        <Title
          animate={inView ? ['opacityIn'] : 'initial'}
          variants={titleAnimation}
        >
          {dictionary.get('dragToExploreMore')}
        </Title>
        <LineWrapper>
          <Line
            animate={inView ? ['opacityIn', 'transformIn'] : 'initial'}
            variants={lineAnimation}
          />
        </LineWrapper>
      </TitleWrapper>
      <SliderWrapper
        animate={inView ? ['opacityIn', 'transformIn'] : 'initial'}
        ref={sliderWrapperRef}
        variants={wrapperAnimation}
      >
        <Slider
          drag="x"
          dragConstraints={{
            left: -sliderConstraints,
            right: 0,
          }}
          initial={{ x: 0 }}
          ref={sliderRef}
          style={{ x }}
        >
          {imageSources.map((image, index) => (
            <ImageWrapper
              aspectRatio={image.fluid.aspectRatio}
              key={`image-${index}`}
              onHoverEnd={onHoverEnd}
              onHoverStart={onHoverStart}
              onTap={(): void => {
                onTap(image.fluid.src)
              }}
              variants={slideImageAnimation}
            >
              <Img fluid={image.fluid} loading="eager" />
            </ImageWrapper>
          ))}
        </Slider>
      </SliderWrapper>
    </StyledCarousel>
  )
}

const StyledCarousel = styled.div`
  ${spacing()};
  overflow: hidden;
`

const TitleWrapper = styled.div`
  ${grid()};
`

const Title = styled(motion.p)`
  ${fullwidthGridColumn()};
  font-size: ${rem(10)};
  font-weight: 700;
  line-height: ${rem(16)};
  text-transform: uppercase;
  padding: 0 0 ${rem(4)};
  letter-spacing: 0.06em;
  width: 100%;
`

const LineWrapper = styled.div`
  overflow: hidden;
  width: ${rem(248)};
`

const Line = styled(motion.div)`
  width: 100%;
  border-bottom: 1px solid ${Colors.SHIP_GREY};
  margin: 0 0 ${rem(16)};
  display: block;
`

const SliderWrapper = styled(motion.div)`
  max-width: ${rem(spacings.sectionMaxWidth)};
  margin: 0 auto;
  padding: 0 0 0 ${rem(spacings.side.root)};
  display: flex;
  align-items: stretch;

  ${mediaQueries.tablet} {
    padding: 0 0 0 ${rem(spacings.side.tablet)};
  }
`

const Slider = styled(motion.div)`
  display: flex;
  flex-wrap: nowrap;
`

interface ImageWrapperProps {
  aspectRatio: number
}

const ImageWrapper = styled(motion.div)<ImageWrapperProps>`
  display: block;
  height: ${rem(464)};
  width: ${({ aspectRatio }): string => rem(464 * aspectRatio)};

  ${mediaQueries.tablet} {
    height: ${rem(760)};
    width: ${({ aspectRatio }): string => rem(760 * aspectRatio)};
  }

  > * {
    pointer-events: none;
  }
`

export default Carousel
